import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeIntegrationGetStationStatsCountryViewers } from '@mi-front/api-v2';
import { toTimestamp } from '@mi-front/core';
import { StreamEl } from '@mi-front/ui';
import { useMemo } from 'react';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { useStreamSettingsStore } from '../../../stores/stream-screen-settings.store';
import { useFocusRange } from '../../../stores/stream-screen.store';
import { YoutubeStatsWaveformGraphSvg } from './charts/YoutubeStatsWaveformGraphSvg';
import { YoutubeViewersPoint } from './utils/youtube-stats-waveform-types';

/**
 * Renders the YouTube viewers overlay line over the stream waveform.
 */
export function YoutubeStatsWaveformGraph() {
  const station = useActiveStation();
  const auditSettings = useStreamSettingsStore((state) => state.audit);
  const range = useStreamSegmentsRange();
  const focusRange = useFocusRange();

  const { data: youtubeStats, isLoading } = useYoutubeIntegrationGetStationStatsCountryViewers(
    station.id,
    {
      from: new Date(range.from).toISOString(),
      to: new Date(range.to).toISOString(),
    },
    {
      query: {
        enabled:
          auditSettings.ytstats &&
          Boolean(station.id) &&
          Boolean(station.youtubeChannelId) &&
          range.from > 0 &&
          range.to > 0,
      },
    },
  );

  const selectedChannelId = useMemo(() => {
    const channels = youtubeStats?.metadata?.channels;
    if (!channels) {
      return station.youtubeChannelId ?? null;
    }

    const availableChannelIds = Object.keys(channels);
    const selfChannelId = availableChannelIds.find((channelId) => channels[channelId]?.isSelf);
    if (selfChannelId) {
      return selfChannelId;
    }

    if (station.youtubeChannelId && availableChannelIds.includes(station.youtubeChannelId)) {
      return station.youtubeChannelId;
    }

    return station.youtubeChannelId ?? null;
  }, [station.youtubeChannelId, youtubeStats?.metadata?.channels]);

  const processedData = useMemo(() => {
    if (!selectedChannelId || !youtubeStats?.timeSeries?.length) {
      return [];
    }

    return youtubeStats.timeSeries
      .map((row) => {
        const rawTime = row.time;
        if (typeof rawTime !== 'string') {
          return null;
        }

        const timestamp = Number(toTimestamp(rawTime));
        const rawViewers = row[selectedChannelId];
        const numericViewers =
          typeof rawViewers === 'number'
            ? rawViewers
            : typeof rawViewers === 'string'
              ? Number(rawViewers)
              : NaN;
        const viewers =
          Number.isFinite(numericViewers) && !Number.isNaN(numericViewers) ? numericViewers : 0;

        return {
          timestamp,
          viewers,
          time: rawTime,
        };
      })
      .filter((point): point is YoutubeViewersPoint => Boolean(point));
  }, [selectedChannelId, youtubeStats?.timeSeries]);

  const stepMinutes = useMemo(() => {
    const focusDurationMs = Math.max(focusRange.to - focusRange.from, 0);
    const thirtyMinutesMs = 30 * 60 * 1000;
    return focusDurationMs > thirtyMinutesMs ? 2 : 1;
  }, [focusRange.from, focusRange.to]);

  return station.youtubeChannelId && !isLoading ? (
    <StreamEl
      key={'yt-stats'}
      ts={toTimestamp(range.from)}
      position="bottom"
      fullHeight
      style={{
        pointerEvents: 'none',
      }}
    >
      {(scale) => {
        const startX = scale(toTimestamp(range.from)) || 0;
        const endX = scale(toTimestamp(range.to)) || 0;
        const width = Math.abs(startX - endX);
        return (
          <YoutubeStatsWaveformGraphSvg
            width={width}
            height={200}
            scale={scale}
            rangeFrom={range.from}
            stepMinutes={stepMinutes}
            processedData={processedData}
          />
        );
      }}
    </StreamEl>
  ) : null;
}
